import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { useCausas } from '@/hooks/useCausas';
import type { AvanceSubPartida } from '@adelante/shared';
import { CircleNotch, XCircle } from '@phosphor-icons/react';
import { useEffect, useState } from 'react';

interface Props {
  /** Sub-partida sobre la que se marca el NC; null = cerrado. */
  subPartida: AvanceSubPartida | null;
  pending: boolean;
  onClose: () => void;
  onConfirmar: (causaCodigo: string, nota: string | null) => void;
}

/**
 * Dialog para marcar una sub-partida como "No cumplió" (NC).
 * El operario elige una causa del catálogo (las que aplican a NC) y, opcional,
 * agrega una nota. La causa es obligatoria.
 */
export function RegistrarNCDialog({ subPartida, pending, onClose, onConfirmar }: Props) {
  const causasQ = useCausas();
  const causas = (causasQ.data?.data ?? []).filter((c) => c.aplica_nc && c.activo);

  const [causaSel, setCausaSel] = useState<string | null>(null);
  const [nota, setNota] = useState('');

  // Reiniciar el form al abrir/cambiar de sub-partida (precarga la causa previa).
  useEffect(() => {
    if (subPartida) {
      setCausaSel(subPartida.nc_causa);
      setNota(subPartida.nc_nota ?? '');
    }
  }, [subPartida]);

  const abierto = subPartida !== null;

  function handleOpenChange(open: boolean) {
    if (!open && !pending) onClose();
  }

  function handleConfirmar() {
    if (!causaSel) return;
    onConfirmar(causaSel, nota.trim() || null);
  }

  return (
    <Dialog open={abierto} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-[440px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <XCircle className="h-5 w-5 text-red-500" weight="fill" aria-hidden="true" />
            Marcar no cumplido
          </DialogTitle>
          <DialogDescription>
            {subPartida ? (
              <span>
                <span className="font-mono text-xs">{subPartida.codigo}</span> · {subPartida.nombre}
              </span>
            ) : (
              ''
            )}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div>
            <Label className="mb-2 block text-xs">Causa del no cumplimiento</Label>
            {causasQ.isLoading && (
              <div className="flex items-center gap-2 py-3 text-sm text-muted-foreground">
                <CircleNotch className="h-4 w-4 animate-spin" weight="bold" aria-hidden="true" />
                Cargando causas…
              </div>
            )}
            <div className="grid grid-cols-1 gap-2">
              {causas.map((c) => {
                const activa = causaSel === c.codigo;
                return (
                  <button
                    key={c.codigo}
                    type="button"
                    onClick={() => setCausaSel(c.codigo)}
                    className={`flex items-center gap-2 rounded-md border px-3 py-2.5 text-left text-sm transition-colors ${
                      activa
                        ? 'border-red-400 bg-red-50 text-red-800'
                        : 'border-border bg-white hover:bg-zinc-50'
                    }`}
                  >
                    <span className="font-mono text-xs text-muted-foreground">{c.codigo}</span>
                    <span>{c.descripcion}</span>
                  </button>
                );
              })}
            </div>
            {!causasQ.isLoading && causas.length === 0 && (
              <p className="rounded-md border border-dashed border-border p-3 text-center text-xs text-muted-foreground">
                No hay causas de NC configuradas.
              </p>
            )}
          </div>

          <div>
            <Label htmlFor="nc-nota" className="mb-1.5 block text-xs">
              Nota (opcional)
            </Label>
            <Textarea
              id="nc-nota"
              rows={2}
              placeholder="Detalle de lo que pasó…"
              value={nota}
              onChange={(e) => setNota(e.target.value)}
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={pending}>
            Cancelar
          </Button>
          <Button variant="destructive" onClick={handleConfirmar} disabled={!causaSel || pending}>
            {pending ? (
              <>
                <CircleNotch
                  className="mr-2 h-4 w-4 animate-spin"
                  weight="bold"
                  aria-hidden="true"
                />
                Guardando…
              </>
            ) : (
              'Marcar NC'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
